import React, { useCallback, useEffect, useMemo, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import api from "../../services/api";
import Layout from "../common/Layout";
import LoadingSpinner from "../common/LoadingSpinner";
import { useAuth } from "../../contexts/AuthContext";
import { formatInUserTimezone } from "../../utils/timezone";

const PAGE_SIZE = 50;

const READ_FILTERS = [
  { key: "all", label: "All" },
  { key: "unread", label: "Unread" },
  { key: "read", label: "Read" },
];

const TYPE_FILTERS = [
  { key: "all", label: "All Types" },
  { key: "reminder", label: "Reminders" },
  { key: "digest", label: "Digests" },
  { key: "system", label: "System" },
];

const DATE_FILTERS = [
  { key: "all", label: "All time" },
  { key: "today", label: "Today" },
  { key: "7", label: "Last 7 days" },
  { key: "30", label: "Last 30 days" },
];

function isoDaysAgo(days) {
  const d = new Date();
  d.setUTCDate(d.getUTCDate() - Number(days));
  return d.toISOString().slice(0, 19).replace("T", " ");
}

function isoTodayStart() {
  const d = new Date();
  d.setUTCHours(0, 0, 0, 0);
  return d.toISOString().slice(0, 19).replace("T", " ");
}

function NotificationsPage() {
  const navigate = useNavigate();
  const { user } = useAuth();
  const [searchParams, setSearchParams] = useSearchParams();

  const role = String(user?.role || "").trim();
  const isManagerOrAdmin = [
    "Admin",
    "SM",
    "Sales Owner",
    "Sales Manager",
  ].includes(role);

  const [tab, setTab] = useState(
    searchParams.get("tab") === "team" && isManagerOrAdmin ? "team" : "mine",
  );
  const [readFilter, setReadFilter] = useState("all");
  const [typeFilter, setTypeFilter] = useState("all");
  const [dateFilter, setDateFilter] = useState("all");

  const [items, setItems] = useState([]);
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(1);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [selectionMode, setSelectionMode] = useState(false);
  const [selectedIds, setSelectedIds] = useState(new Set());

  const load = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const params = {
        page,
        limit: PAGE_SIZE,
        scope: tab === "team" && isManagerOrAdmin ? "team" : "mine",
      };
      if (readFilter === "unread") params.isRead = false;
      else if (readFilter === "read") params.isRead = true;
      if (typeFilter !== "all") params.type = typeFilter;
      if (dateFilter === "today") params.since = isoTodayStart();
      else if (dateFilter !== "all") params.since = isoDaysAgo(dateFilter);

      const res = await api.getNotificationsPaginated(params);
      setItems(Array.isArray(res?.data) ? res.data : []);
      setTotal(Number(res?.total || 0));
    } catch (err) {
      console.error("Failed to load notifications:", err);
      setError("Failed to load notifications");
      setItems([]);
      setTotal(0);
    } finally {
      setLoading(false);
    }
  }, [readFilter, typeFilter, dateFilter, page, tab, isManagerOrAdmin]);

  useEffect(() => {
    load();
  }, [load]);

  useEffect(() => {
    setPage(1);
    setSelectedIds(new Set());
  }, [readFilter, typeFilter, dateFilter, tab]);

  const handleOpen = async (item) => {
    if (selectionMode) {
      setSelectedIds((prev) => {
        const next = new Set(prev);
        if (next.has(item.notification_id)) next.delete(item.notification_id);
        else next.add(item.notification_id);
        return next;
      });
      return;
    }

    const wasUnread = !item.is_read || Number(item.is_read) === 0;
    if (wasUnread) {
      try {
        await api.markNotificationRead(item.notification_id);
        setItems((prev) =>
          prev.map((row) =>
            row.notification_id === item.notification_id
              ? { ...row, is_read: 1 }
              : row,
          ),
        );
      } catch {}
    }
    if (item.lead_id) {
      navigate("/leads", {
        state: {
          openLeadId: item.lead_id,
          focusSection: "next-activities",
          nextActivityId: item.next_activity_id || null,
        },
      });
    }
  };

  const handleMarkAllRead = async () => {
    try {
      await api.markAllNotificationsRead();
      await load();
      setSelectedIds(new Set());
    } catch {}
  };

  const handleMarkSelectedRead = async () => {
    if (selectedIds.size === 0) return;
    try {
      await api.markManyNotificationsRead([...selectedIds]);
      setSelectedIds(new Set());
      await load();
    } catch {}
  };

  const totalPages = Math.max(1, Math.ceil(total / PAGE_SIZE));

  const visibleUnread = useMemo(
    () => items.filter((i) => !i.is_read || Number(i.is_read) === 0).length,
    [items],
  );

  return (
    <Layout
      title="Notifications"
      subtitle="Review reminder history and team activity"
    >
      <div className="notifications-page">
        {/* Tabs */}
        <div className="notifications-tabs">
          <button
            type="button"
            className={`notifications-tab ${tab === "mine" ? "active" : ""}`}
            onClick={() => setTab("mine")}
          >
            <i className="fas fa-user" /> Mine
          </button>
          {isManagerOrAdmin && (
            <button
              type="button"
              className={`notifications-tab ${tab === "team" ? "active" : ""}`}
              onClick={() => setTab("team")}
            >
              <i className="fas fa-users" /> Team
            </button>
          )}
        </div>

        {/* Toolbar */}
        <div className="notifications-toolbar">
          <div className="notifications-filter-row">
            <div className="notifications-filter-group">
              {READ_FILTERS.map((f) => (
                <button
                  key={f.key}
                  type="button"
                  className={`notifications-chip ${
                    readFilter === f.key ? "active" : ""
                  }`}
                  onClick={() => setReadFilter(f.key)}
                >
                  {f.label}
                </button>
              ))}
            </div>

            <select
              className="filter-select"
              value={typeFilter}
              onChange={(e) => setTypeFilter(e.target.value)}
            >
              {TYPE_FILTERS.map((f) => (
                <option key={f.key} value={f.key}>
                  {f.label}
                </option>
              ))}
            </select>

            <select
              className="filter-select"
              value={dateFilter}
              onChange={(e) => setDateFilter(e.target.value)}
            >
              {DATE_FILTERS.map((f) => (
                <option key={f.key} value={f.key}>
                  {f.label}
                </option>
              ))}
            </select>
          </div>

          <div className="notifications-action-row">
            {selectionMode && (
              <button
                type="button"
                className="btn btn-primary btn-sm"
                onClick={handleMarkSelectedRead}
                disabled={selectedIds.size === 0}
              >
                <i className="fas fa-check" /> Mark {selectedIds.size} read
              </button>
            )}

            <button
              type="button"
              className="btn btn-secondary btn-sm"
              onClick={() => {
                setSelectionMode((v) => !v);
                setSelectedIds(new Set());
              }}
            >
              {selectionMode ? (
                <>
                  <i className="fas fa-times" /> Cancel
                </>
              ) : (
                <>
                  <i className="fas fa-check-square" /> Select
                </>
              )}
            </button>

            <button
              type="button"
              className="btn btn-secondary btn-sm"
              onClick={handleMarkAllRead}
            >
              <i className="fas fa-check-double" /> Mark all read
            </button>
          </div>
        </div>

        {/* Body */}
        {loading ? (
          <LoadingSpinner message="Loading notifications..." />
        ) : error ? (
          <div className="error-message">
            <i className="fas fa-exclamation-circle" /> {error}
          </div>
        ) : items.length === 0 ? (
          <div className="empty-state">
            <i className="fas fa-bell-slash" />
            <h3>No notifications</h3>
            <p>Nothing matches the current filters.</p>
          </div>
        ) : (
          <>
            <div className="notifications-list">
              {items.map((item) => {
                const isUnread =
                  !item.is_read || Number(item.is_read) === 0;
                const isSelected = selectedIds.has(item.notification_id);

                return (
                  <button
                    key={item.notification_id}
                    type="button"
                    className={`notification-row ${
                      isUnread ? "is-unread" : "is-read"
                    } ${isSelected ? "is-selected" : ""} notification-type-${
                      item.type
                    }`}
                    onClick={() => handleOpen(item)}
                  >
                    {selectionMode && (
                      <span
                        className="notification-row-checkbox"
                        onClick={(e) => {
                          e.stopPropagation();
                          setSelectedIds((prev) => {
                            const next = new Set(prev);
                            if (next.has(item.notification_id))
                              next.delete(item.notification_id);
                            else next.add(item.notification_id);
                            return next;
                          });
                        }}
                      >
                        <input
                          type="checkbox"
                          checked={isSelected}
                          readOnly
                        />
                      </span>
                    )}

                    <div className="notification-row-left">
                      <div className="notification-row-title">
                        {item.title}
                      </div>
                      {item.body && (
                        <div className="notification-row-body">
                          {item.body}
                        </div>
                      )}
                      <div className="notification-row-meta">
                        <span className="notification-row-type">
                          {item.type}
                        </span>
                        {item.lead_company && (
                          <span className="notification-row-lead">
                            {item.lead_company}
                          </span>
                        )}
                        {tab === "team" && item.owner_name && (
                          <span className="notification-row-owner">
                            <i className="fas fa-user" /> {item.owner_name}
                          </span>
                        )}
                                              <span className="notification-row-date">
                          {formatInUserTimezone(item.created_at)}
                        </span>
                      </div>
                    </div>

                    {/* R6 (additive) — action button */}
                    {item.action_url && (
                      <button
                        type="button"
                        className="r6-notif-action"
                        onClick={(e) => {
                          e.stopPropagation();
                          navigate(item.action_url);
                        }}
                      >
                        {item.action_label || "Manage"}
                      </button>
                    )}

                    <div className="notification-row-right">
                      {isUnread && (
                        <span className="notification-row-unread-dot" />
                      )}
                      {!selectionMode && (
                        <i className="fas fa-chevron-right" />
                      )}
                    </div>
                  </button>
                );
              })}
            </div>

            <div className="notifications-pagination">
              <button
                type="button"
                className="btn btn-secondary btn-sm"
                disabled={page <= 1}
                onClick={() => setPage((p) => Math.max(1, p - 1))}
              >
                <i className="fas fa-chevron-left" /> Prev
              </button>
              <span>
                Page {page} of {totalPages} · {total} total
                {visibleUnread > 0 ? ` · ${visibleUnread} unread on page` : ""}
              </span>
              <button
                type="button"
                className="btn btn-secondary btn-sm"
                disabled={page >= totalPages}
                onClick={() => setPage((p) => p + 1)}
              >
                Next <i className="fas fa-chevron-right" />
              </button>
            </div>
          </>
        )}
      </div>
    </Layout>
  );
}

export default NotificationsPage;